import React from 'react';
import * as Yup from 'yup';
import { useState } from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
import { Stack, TextField, IconButton, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { makeStyles } from '@material-ui/core';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import EyeOpen from '../../../assets/images/eyeon.svg';
import EyeClose from '../../../assets/images/eyeoff.svg';
import { AUTH_POST_PASSWORD_RESET } from '../../../config/api';
import http from '../../../config/http';
import { _route } from '../../../utils';

const useStyles = makeStyles(() => ({
  textField: {
    padding: '0px!important',
    fontSize: '16px!important',
    fontFamily: 'Inter !important',
    fontWeight: '400',
    marginTop: '8px!important',
    transition: '.3s',
    '& .MuiInputLabel-formControl': {
      fontSize: '14px',
      lineHeight: '1em',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      // border: '1px solid #5433FF',
    },
    '& .MuiFormHelperText-root': {
      letterSpacing: '0 !important',
      fontFamily: 'Inter',
      margin: '6px 0 0 0 !important',
      fontWeight: '500',
    },
    '& .MuiOutlinedInput-root .MuiInputBase-input': {
      borderRadius: '8px',
      background: '#fff',
      fontSize: '14px !important',
      // border: '1px solid #D0D5DD',
      boxShadow: ' 0px 1px 2px rgb(16 24 40 / 5%)',
      padding: '13.5px 14px',
      transition: '.3s',
      '&:focus': {
        transition: '.3s',
        outline: '0',
        boxShadow: 'inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 8px rgb(102 175 233 / 60%)',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px !important',
    },
  },
}));
const buttonStyles = {
  borderRadius: '10px',
  boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  width: '100%',
  padding: '10px 14px',
  color: '#fff',
  transition: '0.2s',
  marginTop: '5px',
  fontWeight: '500',
  backgroundColor: '#5433ff',
  borderColor: '#5433ff',
  border: '1px solid #5433ff',
  fontFamily: 'Inter',
  letterSpacing: '0',
  textTransform: 'inherit',
  '&:hover': {
    backgroundColor: '#5433FF',
    borderColor: '#5433FF',
    opacity: '0.9',
    boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  },
  '&:focus': {
    backgroundColor: '#5433FF',
    borderColor: '#5433FF',
    opacity: '0.9',
    boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  },
  '&:visited': {
    backgroundColor: '#5433FF',
    borderColor: '#5433FF',
    opacity: '0.9',
    boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  },
};
export default function ResetForm({ token }) {
  const classes = useStyles();
  const history = useHistory();
  const [showPassword, setShowPassword] = useState(false);

  const passwordRegExp = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;

  const RegisterSchema = Yup.object().shape({
    password: Yup.string().required('Password is required').matches(passwordRegExp, {
      message: 'Password must be at least eight characters, at least one letter, one number and one special character.',
    }),
    retypePassword: Yup.string()
      .required('Please retype your password.')
      .oneOf([Yup.ref('password')], 'Your passwords do not match.'),
  });

  const formik = useFormik({
    initialValues: {
      password: '',
      retypePassword: '',
    },
    validationSchema: RegisterSchema,
    onSubmit: async (_e, { setSubmitting }) => {
      try {
        await http.post(AUTH_POST_PASSWORD_RESET, { token, password: values.password });
        resetForm();
        toast.success('Password changed successfully');
        history.push(_route('login'));
      } catch (err) {
        toast.error(err.response.data.message);
      }
      setSubmitting(false);
    },
  });

  const { errors, touched, handleSubmit, values, isSubmitting, getFieldProps, resetForm } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <div className="customLabel mgTop">New Password*</div>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your password"
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                    {showPassword ? <img src={EyeOpen} /> : <img src={EyeClose} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
            className={classes.textField}
            InputLabelProps={{ shrink: false }}
          />
          <div className="customLabel mgTop">Confirm Password*</div>
          <TextField
            fullWidth
            autoComplete="confirm-password"
            type="password"
            placeholder="Confirm password"
            {...getFieldProps('retypePassword')}
            error={Boolean(touched.retypePassword && errors.retypePassword)}
            helperText={touched.retypePassword && errors.retypePassword}
            className={classes.textField}
            InputLabelProps={{ shrink: false }}
          />

          <LoadingButton fullWidth size="large" type="submit" sx={buttonStyles} variant="contained" loading={isSubmitting}>
            Reset Password
          </LoadingButton>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
